import { ICellRendererParams } from "ag-grid-community";
import { Check, X } from "lucide-react";
import { MouseEvent, useMemo } from "react";

import type { RequestRow } from "../requestLogGridTypes";

import { HydrationPendingCell, isRequestRowHydrating } from "./HydrationPendingCell";

import { useStructuredSearchStore } from "@/components/StructuredSearch/stores";
import { appendNestedKeyEqualsStructuredFilter } from "@/components/SidebarSearchLayout/Sidebar/SecondarySidebar/SearchBar/appendNestedKeyEqualsStructuredFilter";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import {
  buildSignalStructuredFilterDetail,
  getSignalBubbleClassName,
  hydrateSidebarSignalBubbles,
  type SidebarSignalBubble,
} from "@/routes/request-logs/search/signal-search-utils";
import { useWorkspaceSignalNames } from "@/routes/request-logs/search/useWorkspaceSignalNames";

export const SignalsCellRenderer = ({ data }: ICellRendererParams<RequestRow>) => {
  const userToken = useUserToken();
  const { activeWorkspaceId } = useUser();
  const structuredSearchStore = useStructuredSearchStore();
  const signalNamesById = useWorkspaceSignalNames(userToken, activeWorkspaceId);
  const isHydrating = isRequestRowHydrating(data);

  const signals = useMemo(
    () => hydrateSidebarSignalBubbles(data?.metadata, signalNamesById),
    [data?.metadata, signalNamesById],
  );

  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: signals.length,
    keepMinOne: true,
  });

  const handleSignalClick = (event: MouseEvent<HTMLButtonElement>, signal: SidebarSignalBubble) => {
    event.preventDefault();
    event.stopPropagation();
    appendNestedKeyEqualsStructuredFilter(
      structuredSearchStore,
      buildSignalStructuredFilterDetail(signal.signalId, signal.name, signal.isTrue),
    );
  };

  const renderSignal = (signal: SidebarSignalBubble) => {
    const ValueIcon = signal.isTrue ? Check : X;

    return (
      <BasicTooltip key={signal.signalId} content="Add Signal filter">
        <button
          type="button"
          className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 ${getSignalBubbleClassName()}`}
          onClick={event => handleSignalClick(event, signal)}
        >
          <ValueIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
          {signal.name}
        </button>
      </BasicTooltip>
    );
  };

  if (isHydrating && signals.length === 0) {
    return <HydrationPendingCell />;
  }

  if (signals.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full min-w-0 flex-1 items-center gap-1"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute flex w-full gap-1"
        aria-hidden="true"
      >
        {signals.map(signal => renderSignal(signal))}
      </div>
      {signals.slice(0, visibleCount).map(signal => renderSignal(signal))}
      {hiddenCount > 0 && (
        <BasicTooltip
          content={signals
            .slice(visibleCount)
            .map(signal => signal.name)
            .join(", ")}
        >
          <span>
            <Badge variant={BadgeVariant.GRAY} size={BadgeSize.SM}>
              +{hiddenCount}
            </Badge>
          </span>
        </BasicTooltip>
      )}
    </div>
  );
};
